import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LogoMark } from "./Logo";
import { BRAND } from "@/lib/content";
import { useExtras } from "@/lib/texts";

const MIN_TIME = 1400;
const MAX_TIME = 4000;
const EASE = [0.16, 1, 0.3, 1];

export const SplashScreen = () => {
    const { ready, heroImage } = useExtras();
    const [visible, setVisible] = useState(true);
    const [imgLoaded, setImgLoaded] = useState(false);
    const startRef = useRef(Date.now());

    useEffect(() => {
        if (!heroImage) {
            setImgLoaded(true);
            return;
        }
        setImgLoaded(false);
        let done = false;
        const img = new Image();
        img.onload = img.onerror = () => {
            if (!done) {
                done = true;
                setImgLoaded(true);
            }
        };
        img.src = heroImage;
        return () => {
            done = true;
        };
    }, [heroImage]);

    useEffect(() => {
        if (!visible) return;
        let timer;
        const check = () => {
            const elapsed = Date.now() - startRef.current;
            if (elapsed >= MAX_TIME || (ready && imgLoaded && elapsed >= MIN_TIME)) {
                setVisible(false);
                return;
            }
            timer = setTimeout(check, 100);
        };
        check();
        return () => clearTimeout(timer);
    }, [ready, imgLoaded, visible]);

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    data-testid="splash-screen"
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-[#F9F7F2]"
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease: "easeInOut" }}
                >
                    <div className="grain pointer-events-none absolute inset-0" />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 14 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: EASE }}
                        className="relative flex flex-col items-center"
                    >
                        <LogoMark className="h-24 w-auto sm:h-28" />
                        <motion.img
                            src="/logo-tricolore.png"
                            alt=""
                            aria-hidden="true"
                            data-testid="splash-tricolore"
                            initial={{ scaleX: 0.2, opacity: 0 }}
                            animate={{ scaleX: 1, opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
                            className="mt-3 h-6 w-auto sm:h-7"
                        />
                        <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="mt-7 font-mono-alt text-[10px] uppercase tracking-[0.35em] text-stone-500"
                        >
                            Impresa Edile · dal {BRAND.since}
                        </motion.span>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
